// Ability damage/heal/hit formulas, derived by parsing the mustad.io
// /api/abilities `info` strings instead of a hand-maintained switch.
//
// The API encodes each ability's formula in a regular grammar, e.g.
//   "... Element: Fire. Effect: Damage Faith(MA * 16)."        (faith magic)
//   "... Hit: Faith(MA + 175)%. Effect: Add Sleep."            (status %)
//   "... Effect: Damage (PA * WP)."                            (physical)
//   "... Effect: Damage (PA * Random(1,8)); ..."              (random range)
//   "... Effect: Damage (MA * ((MA + 121) / 2) * ((100 - Faith) / 100)
//                          * ((100 - TargetFaith) / 100))."    (un-truth)
//
// Rather than a general arithmetic evaluator (the `/` operator means
// floor-now in stat terms but a deferred fraction in faith terms), we
// RECOGNIZE a fixed set of formula families and evaluate each with its own
// correct integer-flooring. Anything we don't recognize returns null, so the
// caller can fall back to the legacy switch.
//
// parseAbilityInfo(info) -> model | null
// renderAbility(model, ctx, mods?) -> display string
//   ctx:  { ma, pa, speed, brave, faith, targetFaith, wp, maxHp }
//   mods: { strengthen:Set<element>, attackUp:bool, magicAttackUp:bool,
//           martialArts:bool }
//         Attacker-only modifiers applied to DAMAGE results: Strengthen
//         (×5/4 when the attack's element is in the set), Attack UP /
//         Magic Attack UP (×4/3, physical vs magical), and Martial Arts
//         (×3/2, on PA-stat Punch Art ability formulas only — flagged
//         `maBoost`). They scale the XA term of the formula with the game's
//         per-step flooring — NOT the final number — and only one stat
//         instance, matching the BMG.
//
// `faith` is the caster's Faith, `targetFaith` the defender's (0-100). The
// Faith(X) macro means floor(faith * targetFaith * X / 10000).

const idiv = (a, b) => Math.floor(a / b);
const identity = (x) => x;
// A Random(lo,hi) factor rolled at position `at` in [0, 1): lo + ⌊at × (hi − lo + 1)⌋. The AI's trial rolls at the
// middle (at = 0.5: Throw Stone's Random(1,2) counts as 2, FFTBG's PA-12 Squire valued a stone at 24 on a Good
// zodiac match); a real hit rolls anywhere. Without ctx.rollAt a formula returns its [min, max] range for display.
const rollOf = (lo, hi, at) => lo + Math.floor(at * (hi - lo + 1));

// Martial Arts (the support ability; the Monk's innate is identical) raises the
// modifiable PA term by ×3/2 (BMG MOD 2 step 4: "MartialArtsPA = [PA * 3/2]").
// Unlike the bare-hand basic ATTACK (barehanded-only — handled in damage.js),
// Punch Arts are non-weapon-elemental, so the bonus applies even with a weapon
// equipped. We only ever apply it to PA-stat *ability* models (flagged
// `maBoost` by parseAbilityInfo); the weapon/unarmed/Jump models from damage.js
// don't carry the flag, so the unarmed attack's own baked ×1.5 isn't doubled
// and Jump/weapon attacks (which the BMG exempts) stay untouched.
const ma32 = (v) => Math.floor((v * 3) / 2);
const maEligible = (model, mods) => !!(mods && mods.martialArts && model && model.maBoost);

const PREFIX = {
  Damage: '',
  Heal: 'Heal: ',
  HealMP: 'HealMP: ',
  DamageMP: 'DamageMP: ',
  AbsorbHP: 'AbsorbHP: ',
  AbsorbMP: 'AbsorbMP: ',
};

const STAT_TOKEN = { MA: 'ma', PA: 'pa', Speed: 'speed', Brave: 'brave', WP: 'wp' };
const statKey = (tok) => STAT_TOKEN[tok];

// Each family's eval takes (ctx, xa) where `xa` scales the XA term (the part
// the attacker modifiers apply to). xa defaults to identity (no mods). `stat`
// is the primary attack stat, used to decide physical vs magical.
function matchExpr(expr) {
  expr = expr.trim();
  let m;

  // Faith(MA * Q)  -> faith magic damage/heal
  if ((m = expr.match(/^Faith\(\s*(MA|PA) \* (\d+)\s*\)$/))) {
    const s = statKey(m[1]); const q = +m[2];
    // (mid: what the game does to the product before Faith, an elemental Half: BATTLE.BIN's magic routine 0x80188D84
    // multiplies XA × YA, applies the elements, then Faith; FFTBG's MA-9 Fire 4 on a Fire-half foe was 315 → 157 → 50)
    return { stat: s, faithProduct: true, eval: (c, xa = identity, zod, mid = identity) => Math.floor((c.faith * c.targetFaith * mid(xa(c[s]) * q)) / 10000) };
  }

  // (STAT * STAT2)   e.g. PA * WP, MA * MA
  if ((m = expr.match(/^\(?\s*(MA|PA|Speed|Brave|WP) \* (MA|PA|Speed|Brave|WP)\s*\)?$/))) {
    const a = statKey(m[1]); const b = statKey(m[2]);
    return { stat: a, eval: (c, xa = identity) => xa(c[a]) * c[b] };
  }

  // (STAT * N)
  if ((m = expr.match(/^\(?\s*(MA|PA|Speed|Brave) \* (\d+)\s*\)?$/))) {
    const a = statKey(m[1]); const n = +m[2];
    return { stat: a, eval: (c, xa = identity) => xa(c[a]) * n };
  }

  // (A * ((B + C) / 2))  -> punch-art / elemental: A * floor((B+C)/2). Modifiers scale the
  // leading A, not the bracket: in FFTBG's build Wave Fist (PA 12, Martial Arts) did
  // (12 → 18) × 7 = 126, Will-O-Wisp (MA 13, PA 9) did (13 → 16 Good) × 5 = 80 and
  // (13 → 10 Bad) × 5 = 50.
  if ((m = expr.match(/^\(?\s*(MA|PA) \* \(\((MA|PA) \+ (\d+)\) \/ 2\)\s*\)?$/))) {
    const outer = statKey(m[1]); const inner = statKey(m[2]); const cst = +m[3];
    return { stat: outer, eval: (c, xa = identity) => xa(c[outer]) * idiv(c[inner] + cst, 2) };
  }

  // (A / 2) * B  -> floor(A/2) * B
  if ((m = expr.match(/^\(?\s*\(?(MA|PA) \/ 2\)? \* (MA|PA)\s*\)?$/))) {
    const a = statKey(m[1]); const b = statKey(m[2]);
    return { stat: b, eval: (c, xa = identity) => xa(idiv(c[a], 2)) * c[b] };
  }

  // (A * (B / 2))  -> A * floor(B/2). Modifiers scale the leading A: in FFTBG's build Earth
  // Slash (PA 11, Martial Arts, charging target) did (11 → 16 → 20) × 5 = 100.
  if ((m = expr.match(/^\(?\s*(MA|PA) \* \((MA|PA) \/ 2\)\s*\)?$/))) {
    const a = statKey(m[1]); const b = statKey(m[2]);
    return { stat: a, eval: (c, xa = identity) => xa(c[a]) * idiv(c[b], 2) };
  }

  // (PA * (WP + N))  -> sword skills
  if ((m = expr.match(/^\(?\s*(MA|PA) \* \(WP \+ (\d+)\)\s*\)?$/))) {
    const a = statKey(m[1]); const n = +m[2];
    return { stat: a, eval: (c, xa = identity) => xa(c[a]) * (c.wp + n) };
  }

  // (PA * (WP + TargetMA [/ D]))  -> Unyielding Cut's no-equipment branch: WP plus the
  // TARGET's MA, halved since FFTBG's 2026-07-15 patch ("TargetMA / 2", floored like
  // every FFT division). This is the grammar's only DEFENDER-stat term; ctx.targetMa
  // (the raw MA) defaults to 0 when the target loadout is unknown (the equipped branch
  // is used in that case anyway — see the conditional in parseAbilityInfo).
  if ((m = expr.match(/^\(?\s*(MA|PA) \* \(WP \+ TargetMA(?: \/ (\d+))?\)\s*\)?$/))) {
    const a = statKey(m[1]); const d = m[2] ? +m[2] : 1;
    return { stat: a, eval: (c, xa = identity) => xa(c[a]) * (c.wp + idiv(c.targetMa || 0, d)) };
  }

  // (A * (B * Brave / 100))  -> monster brave attacks: A * floor(B*Brave/100). The modifiers
  // (zodiac, Defense Up, Protect, charging…) scale the OUTER stat, not the bracket: FFTBG's own
  // build did 72 for Iron Hawk's Scratch Up (PA 15, Brave 40) through Defense Up + Good zodiac,
  // i.e. (15 → 10 → 12) × 6, where scaling the bracket gives 15 × (6 → 4 → 5) = 75.
  if ((m = expr.match(/^\(?\s*(MA|PA) \* \((MA|PA) \* Brave \/ 100\)\s*\)?$/))) {
    const a = statKey(m[1]); const b = statKey(m[2]);
    return { stat: a, eval: (c, xa = identity) => xa(c[a]) * idiv(c[b] * c.brave, 100) };
  }

  // (STAT * Random(a,b) * 3 / 2)  -> Pummel-style scaled range. The game's routine (BATTLE.BIN 0x80189D74) makes one
  // hit XA + YA, where XA is the stat with the modifiers (Martial Arts, Protect, ...) and YA is ⌊stat / 2⌋ with only
  // zodiac (0x8018659C), then multiplies by the roll: FFTBG's PA-10 Monk Pummelled for
  // (15 + 5) × 5 = 100, not ⌊15 × 5 × 3/2⌋ = 112. With no modifiers it's the help text's stat × roll × 3/2.
  if ((m = expr.match(/^\(?\s*(MA|PA) \* Random\((\d+),(\d+)\) \* 3 \/ 2\s*\)?$/))) {
    const a = statKey(m[1]); const lo = +m[2]; const hi = +m[3];
    const hit = (c, xa, zod) => xa(c[a]) + zod(idiv(c[a], 2));
    return { stat: a, eval: (c, xa = identity, zod = identity) => (c.rollAt != null ? hit(c, xa, zod) * rollOf(lo, hi, c.rollAt) : [hit(c, xa, zod) * lo, hit(c, xa, zod) * hi]) };
  }

  // (STAT * Random(a,b))  -> range "min - max"
  if ((m = expr.match(/^\(?\s*(MA|PA) \* Random\((\d+),(\d+)\)\s*\)?$/))) {
    const a = statKey(m[1]); const lo = +m[2]; const hi = +m[3];
    return { stat: a, eval: (c, xa = identity) => (c.rollAt != null ? xa(c[a]) * rollOf(lo, hi, c.rollAt) : [xa(c[a]) * lo, xa(c[a]) * hi]) };
  }

  // Un-truth: (MA * ((MA + C) / 2) * ((100 - Faith) / 100) * ((100 - TargetFaith) / 100))
  if ((m = expr.match(/^\(?\s*MA \* \(\(MA \+ (\d+)\) \/ 2\) \* \(\(100 - Faith\) \/ 100\) \* \(\(100 - TargetFaith\) \/ 100\)\s*\)?$/))) {
    const cst = +m[1];
    return { stat: 'ma', eval: (c, xa = identity) => Math.floor((xa(c.ma) * idiv(c.ma + cst, 2) * (100 - c.faith) * (100 - c.targetFaith)) / 10000) };
  }

  // (MaxHP * a / b)  -> fraction of max HP (no stat -> no attacker mods)
  if ((m = expr.match(/^\(?\s*MaxHP \* (\d+) \/ (\d+)\s*\)?$/))) {
    const a = +m[1]; const b = +m[2];
    return { stat: null, eval: (c) => idiv(c.maxHp * a, b) };
  }

  // (A + (A * Brave / 100))  -> brave-scaled (Wiznaibus)
  if ((m = expr.match(/^\(?\s*(MA|PA) \+ \(\1 \* Brave \/ 100\)\s*\)?$/))) {
    const a = statKey(m[1]);
    return { stat: a, eval: (c, xa = identity) => xa(c[a] + idiv(c[a] * c.brave, 100)) };
  }

  // (STAT + N)  -> plain additive (Angel/Life Song)
  if ((m = expr.match(/^\(?\s*(MA|PA|Speed) \+ (\d+)\s*\)?$/))) {
    const a = statKey(m[1]); const n = +m[2];
    return { stat: a, eval: (c) => c[a] + n };
  }

  // (N)  -> fixed literal (no stat -> no attacker mods)
  if ((m = expr.match(/^\(?\s*(\d+)\s*\)?$/))) {
    const n = +m[1];
    return { stat: null, eval: () => n };
  }

  return null;
}

// Thrown-weapon power (Speed * ThrownWP). The API names the thrown item but its
// "thrown WP" is fixed per item and NOT in the item stats for shuriken/bombs
// (their equip WP is 0), so key a small table off the thrown item (BMG values,
// matching what legacyAbilityFormula showed). Element from the thrown weapon.
const THROWN_WP = {
  'Giant Axe': 15, 'Wizard Rod': 7, 'Ivory Cane': 12, 'Rune Blade': 14, 'Gold Staff': 6,
  'Holy Lance': 13, 'Morning Star': 16, 'Air Knife': 10, 'Spell Edge': 11,
  Shuriken: 9, 'Burst Bomb': 8, 'Papyrus Codex': 10,
};
const THROWN_ELEM = { 'Ivory Cane': 'Earth', 'Holy Lance': 'Holy', 'Air Knife': 'Wind', 'Papyrus Codex': 'Water' };

export function parseAbilityInfo(info) {
  if (!info) return null;
  // Jump is owned by jumpAttackModel (damage.js) — it needs the Spear ×3/2 and
  // unarmed-brave branches the info encodes as prose, which this grammar can't
  // express. Bail so the dedicated model handles it everywhere.
  if (/\(jumping\)/.test(info)) return null;

  // Throw (Ninja): "Throw <type> (<item>…); Damage (Speed * ThrownWP)" → speed*WP.
  if (/Damage \(Speed \* ThrownWP\)/.test(info)) {
    const item = (info.match(/Throw [\w ]+ \(([^),]+)/) || [])[1];
    const wp = item && THROWN_WP[item.trim()];
    if (wp) {
      return { kind: 'number', verb: 'Damage', element: (item && THROWN_ELEM[item.trim()]) || null,
        stat: 'speed', magical: false, maBoost: false, evalFn: (c, xa = identity) => xa(c.speed) * wp };
    }
  }

  const element = (info.match(/Element:\s*(\w+)/) || [])[1] || null;

  const effect = (info.match(/Effect:\s*(.+)$/) || [])[1] || '';

  // Two-branch equipment conditional (Unyielding Cut):
  //   "If target is equipped with any item except for a Shield, Damage (E1).
  //    If none is equipped, Damage (E2)."
  // E1 fires against an equipped target (the common case); E2 against a bare one
  // (monsters carry no gear, so they take E2, which folds in the target's MA). We
  // pick by ctx.targetEquipped — defaulting to the equipped branch when the
  // target's loadout is unknown (the generic single card and, for now, the sim).
  // Handled here, before the ';'-split loop, because the branches are joined by
  // '. ' and the leading "If …," strip below would otherwise garble them.
  {
    const cond = effect.match(/^If target is equipped[^,]*,\s*Damage\s+(.+?)\.\s+If none is equipped,\s*Damage\s+(.+?)\.?\s*$/);
    if (cond) {
      const e1 = matchExpr(cond[1].trim());
      const e2 = matchExpr(cond[2].trim());
      if (e1 && e2) {
        return {
          kind: 'number', verb: 'Damage', element,
          stat: e1.stat,
          magical: e1.stat === 'ma',
          maBoost: e1.stat === 'pa',
          evalFn: (c, xa = identity) => (c.targetEquipped === false ? e2.eval(c, xa) : e1.eval(c, xa)),
        };
      }
    }
  }

  for (const clause of effect.split(';')) {
    // Strip a leading "If <cond>, " guard so the equip-break punch arts
    // ("Remove target's head equipment; If successful, Damage (PA * WP)") parse.
    // Death/Poison Nail's conditional damage is "(NN)%" and stays skipped below.
    const cm = clause.trim().replace(/^If [^,]+,\s*/, '').match(/^(Damage|Heal|HealMP|DamageMP|AbsorbHP|AbsorbMP)\s+(.+?)\.?\s*$/);
    if (!cm) continue;
    const verb = cm[1];
    const exprText = cm[2].trim();
    if (/^\(?\d+\)?%$/.test(exprText)) continue; // "(100)%" conditional — skip
    const matched = matchExpr(exprText);
    if (matched) {
      return {
        kind: 'number', verb, element,
        stat: matched.stat,
        magical: matched.stat === 'ma',
        // PA-stat ability formulas (Punch Arts: Spin/Wave/Earth Slash damage,
        // Chakra heal) get the Martial Arts ×3/2 — see ma32/maEligible.
        maBoost: matched.stat === 'pa',
        evalFn: matched.eval,
        ...(matched.faithProduct ? { faithProduct: true } : {}),
      };
    }
  }

  // Status spell: success-rate from the Hit: clause. `faith` distinguishes
  // MOD 6 magic (CFa×TFa × (MA+Y)) from plain MOD 1/3/4 (stat + K, no faith).
  // evalFn is the faith-only quick calc used by the single card; the fight
  // view uses computeSuccess() for the full MOD 6 pipeline + breakdown.
  const hit = (info.match(/Hit:\s*([^.]+?)%/) || [])[1];
  if (hit) {
    const tail = hit.split(';').pop().trim();
    let fm;
    if ((fm = tail.match(/^Faith\(\s*(MA|PA) \+ (\d+)\s*\)$/))) {
      const s = statKey(fm[1]); const y = +fm[2];
      return { kind: 'percent', element, faith: true, stat: s, k: y, maBoost: s === 'pa',
        evalFn: (c) => Math.floor((c.faith * c.targetFaith * (c[s] + y)) / 10000) };
    }
    if ((fm = tail.match(/^\(?\s*(MA|PA|Speed) \+ (\d+)\s*\)?$/))) {
      const s = statKey(fm[1]); const k = +fm[2];
      return { kind: 'percent', element, faith: false, stat: s, k, maBoost: s === 'pa',
        evalFn: (c) => c[s] + k };
    }
  }

  return null;
}

// Zodiac compatibility modifies the XA term additively (BMG).
export function zodiacStep(compat) {
  switch (compat) {
    case 'Good':  return (x) => x + Math.floor(x / 4);
    case 'Bad':   return (x) => x - Math.floor(x / 4);
    case 'Best':  return (x) => x + Math.floor(x / 2);
    case 'Worst': return (x) => x - Math.floor(x / 2);
    default:      return identity;
  }
}

// The ordered list of XA-term modifiers for a Damage attack, branching
// physical vs magical, in BMG order (each floored). `mods` carries both the
// attacker side (strengthen, attack/magic up, berserk) and the defender side
// (defense/magic-defend up, protect/shell, charging/sleeping/frog, zodiac).
// Absent fields are no-ops, so the single card (attacker-only mods) and the
// fight view (full mods) share one code path.
const ZODIAC_FACTOR = { Good: '×5/4', Bad: '×3/4', Best: '×3/2', Worst: '×1/2' };

function xaSteps(model, mods) {
  const out = [];
  const mag = model.magical;
  const add = (cond, label, factor, apply) => { if (cond) out.push({ label, factor, apply }); };
  const x = (n, d) => (v) => Math.floor((v * n) / d);

  add(model.element && mods.strengthen && mods.strengthen.has(model.element), 'Strengthen', '×5/4', x(5, 4));
  add(mag ? mods.magicAttackUp : mods.attackUp, mag ? 'Magic Attack Up' : 'Attack Up', '×4/3', x(4, 3));
  add(model.maBoost && mods.martialArts, 'Martial Arts', '×3/2', x(3, 2));
  if (!mag) add(mods.berserk, 'Berserk', '×3/2', x(3, 2));
  add(mag ? mods.magicDefendUp : mods.defenseUp, mag ? 'Magic Defense Up' : 'Defense Up', '×2/3', x(2, 3));
  add(mag ? mods.shell : mods.protect, mag ? 'Shell' : 'Protect', '×2/3', x(2, 3));
  // FFTBG's status help (and its build): Charging +25% from physical attacks, Sleep +50%
  // physical, Frog/Chicken +50% from all attacks.
  if (!mag) {
    add(mods.charging, 'Charging', '×5/4', x(5, 4));
    add(mods.sleeping, 'Sleeping', '×3/2', x(3, 2));
  }
  add(mods.frogChicken, 'Frog/Chicken', '×3/2', x(3, 2));
  if (mods.zodiac && mods.zodiac !== 'Neutral') {
    out.push({ label: `Zodiac ${mods.zodiac}`, factor: ZODIAC_FACTOR[mods.zodiac], apply: zodiacStep(mods.zodiac) });
  }
  // Weak doubles the stat term, last, so it lands before the formula's final floor: FFTBG's Bolt on a
  // Lightning-weak Ahriman did 101, ⌊2880 × (11·2) × 16 / 10000⌋, where doubling the finished 50 gives
  // 100. Half is NOT a stat step: it halves the result (Shiva on an Ice-half Porky did 59 = ⌊119 / 2⌋,
  // where halving the stat 13 → 6 gives 55).
  const el = model.element;
  add(el && mods.weak && mods.weak.has(el), 'Weak', '×2', x(2, 1));
  // Oil doubles a Fire attack's stat term too (BATTLE.BIN 0x80186FF8): FFTBG's MA-8 Fire on an oiled foe was 16 × 16
  // = 256 before Faith, 53, where doubling the finished 26 gives 52
  add(el === 'Fire' && mods.oil, 'Oil', '×2', x(2, 1));
  return out;
}

const scaleResult = (r, fn) => (Array.isArray(r) ? [fn(r[0]), fn(r[1])] : fn(r));
const fmtNum = (r) => (Array.isArray(r) ? `${r[0]} - ${r[1]}` : `${r}`);

// Full damage computation with a per-step breakdown. Only meaningful for the
// Damage verb; heals/percents return their value with no steps. `final` is a
// number or [min,max]; `steps` is [{ label, value }] for the drill-down.
export function computeAbilityDamage(model, ctx, mods = {}) {
  if (!model || model.kind !== 'number' || model.verb !== 'Damage' || !model.stat) {
    // Non-damage numeric verbs (Heal/HealMP/Absorb…). Martial Arts still raises a
    // PA-scaling Punch Art (Chakra's heal) by ×3/2 on its stat term.
    return { final: model ? model.evalFn(ctx, maEligible(model, mods) ? ma32 : identity) : 0, steps: [] };
  }
  const steps = [{ label: 'Base', factor: '', value: fmtNum(model.evalFn(ctx, identity)) }];

  let composed = identity;
  for (const s of xaSteps(model, mods)) {
    const prev = composed;
    composed = (v) => s.apply(prev(v));
    steps.push({ label: s.label, factor: s.factor, value: fmtNum(model.evalFn(ctx, composed)) });
  }
  // (a formula with a second stat term that takes zodiac alone, Pummel's ⌊PA / 2⌋, gets the zodiac step too)
  const zod = mods.zodiac && mods.zodiac !== 'Neutral' ? zodiacStep(mods.zodiac) : identity;
  const el = model.element, halfEl = !!(el && mods.half && mods.half.has(el));
  // Faith magic halves the formula's product before Faith (see matchExpr); everything else halves the result
  const halfFirst = halfEl && model.faithProduct;
  let result = halfFirst ? model.evalFn(ctx, composed, zod, (v) => Math.floor(v / 2)) : model.evalFn(ctx, composed, zod);

  if (el && mods.cancel && mods.cancel.has(el)) { result = scaleResult(result, () => 0);                  steps.push({ label: 'Cancel', factor: '×0', value: fmtNum(result) }); }
  if (halfEl) { if (!halfFirst) result = scaleResult(result, (r) => Math.floor(r / 2)); steps.push({ label: 'Half', factor: '÷2', value: fmtNum(result) }); }
  if (el && mods.absorb && mods.absorb.has(el)) { result = scaleResult(result, (r) => -r);               steps.push({ label: 'Absorbed', factor: '×−1', value: fmtNum(result) }); }

  return { final: result, steps };
}

// Zodiac as the MOD 1/6 success ADDEND (uses both the stat and the constant).
function zodiacAddend(compat, stat, y) {
  switch (compat) {
    case 'Good':  return Math.floor(stat / 4) + Math.floor(y / 4);
    case 'Bad':   return -(Math.floor(stat / 4) + Math.floor(y / 4));
    case 'Best':  return Math.floor(stat / 2) + Math.floor(y / 2);
    case 'Worst': return -(Math.floor(stat / 2) + Math.floor(y / 2));
    default:      return 0;
  }
}

// Success-rate computation with breakdown for status (percent) abilities.
// MOD 6 (faith=true): Magic Attack Up / Magic Defend Up / Shell / Strengthen
// scale the stat, zodiac adds its addend, then × caster faith × target faith.
// MOD 1 (faith=false, e.g. talk/steal): stat + K + zodiac addend, no faith.
export function computeSuccess(model, ctx, mods = {}) {
  const steps = [];
  const Y = model.k;
  let s = ctx[model.stat];
  steps.push({ label: model.stat.toUpperCase(), factor: '', value: `${s}` });

  // Martial Arts raises the PA term of a PA-based status Punch Art (Stigma Magic
  // / Revive / Purification) by ×3/2, like its damage counterpart.
  if (maEligible(model, mods)) { s = ma32(s); steps.push({ label: 'Martial Arts', factor: '×3/2', value: `${s}` }); }

  if (model.faith) {
    if (model.element && mods.strengthen && mods.strengthen.has(model.element)) { s = Math.floor((s * 5) / 4); steps.push({ label: 'Strengthen', factor: '×5/4', value: `${s}` }); }
    if (mods.magicAttackUp) { s = Math.floor((s * 4) / 3); steps.push({ label: 'Magic Attack Up', factor: '×4/3', value: `${s}` }); }
    if (mods.magicDefendUp) { s = Math.floor((s * 2) / 3); steps.push({ label: 'Magic Defense Up', factor: '×2/3', value: `${s}` }); }
    if (mods.shell)         { s = Math.floor((s * 2) / 3); steps.push({ label: 'Shell', factor: '×2/3', value: `${s}` }); }
  }

  const z = zodiacAddend(mods.zodiac, s, Y);
  let raw = s + Y + z;
  steps.push({ label: `+ ${Y} (const)`, factor: '', value: `${s + Y}` });
  if (mods.zodiac && mods.zodiac !== 'Neutral') {
    steps.push({ label: `Zodiac ${mods.zodiac}`, factor: (z >= 0 ? '+' : '') + z, value: `${raw}` });
  }

  let final;
  if (model.faith) {
    final = Math.floor((ctx.faith * ctx.targetFaith * raw) / 10000);
    steps.push({ label: 'Faith', factor: `×${ctx.faith}% ×${ctx.targetFaith}%`, value: `${final}%` });
  } else {
    final = raw;
    steps[steps.length - 1].value = `${final}%`;
  }
  return { final, steps };
}

export function renderAbility(model, ctx, mods) {
  if (!model) return '';
  if (model.kind === 'percent') {
    if (mods && ((mods.zodiac && mods.zodiac !== 'Neutral') || maEligible(model, mods))) return `${computeSuccess(model, ctx, mods).final}%`;
    return `${model.evalFn(ctx, identity)}%`;
  }
  if (model.verb !== 'Damage') return `${PREFIX[model.verb] ?? ''}${model.evalFn(ctx, maEligible(model, mods) ? ma32 : identity)}`;
  return fmtNum(computeAbilityDamage(model, ctx, mods).final);
}

// Convenience: parse + render to a string. Returns '' if unrecognized.
export function abilityDisplay(info, ctx, mods) {
  const model = parseAbilityInfo(info);
  return model ? renderAbility(model, ctx, mods) : '';
}

// Fight-view entry: parse + compute against a specific target. Returns null
// for unrecognized abilities. For damage, includes the numeric `final`, the
// `steps` breakdown, and element/magical flags (for color-coding).
export function abilityVsTarget(info, ctx, mods = {}) {
  const model = parseAbilityInfo(info);
  if (!model) return null;
  if (model.kind === 'percent') {
    if (mods && ((mods.zodiac && mods.zodiac !== 'Neutral') || maEligible(model, mods))) {
      const r = computeSuccess(model, ctx, mods);
      return { kind: 'percent', faith: model.faith, display: `${r.final}%`, steps: r.steps };
    }
    return { kind: 'percent', faith: model.faith, display: `${model.evalFn(ctx, identity)}%`, steps: [] };
  }
  if (model.verb !== 'Damage') {
    return { kind: 'other', verb: model.verb, magical: model.magical, display: `${PREFIX[model.verb] ?? ''}${model.evalFn(ctx, maEligible(model, mods) ? ma32 : identity)}`, steps: [] };
  }
  const { final, steps } = computeAbilityDamage(model, ctx, mods);
  return { kind: 'damage', element: model.element, magical: model.magical, final, display: fmtNum(final), steps };
}

// Slim fallback for the ability forms the parser-first path genuinely CAN'T
// derive — every case here is irreducible, NOT just "not migrated yet":
//   • Jump — its info encodes the Spear ×3/2 and unarmed-brave branches as prose
//     ("If Spear is equipped, increase damage by 50%"); the card/fight view use
//     jumpAttackModel (damage.js) for it, and the sim builds it in simPrep.
//   • Knight breaks (Weapon/Armor/Head/Shield/Magic Break) — units carry these
//     names, but the mustad.io abilities API only has the WotL renames (Weapon
//     Rend, …) under DIFFERENT keys, so there's no info to look up by the live name.
//   • Monster skills (Golem/Toxic Frog/Bio Sanctus/Blind Rage/Confusion Song/
//     Silence Song/Ice Soul) + Hellcry Punch — absent from the abilities API
//     entirely (they ride in toolTips.MonsterSkills at runtime, with no formula).
//   • Elixir — the "Heal (100)%" form the parser skips; shown as "Full Heal".
// Throw + the equip-break punch arts were MIGRATED to parseAbilityInfo and removed.
// Full retirement plan (why each case is stuck, order of attack, verification):
// docs/legacy-ability-formula.md.
// Pure; mirrors the hand-coded values the unit card historically showed. Returns
// { kind: 'damage'|'percent'|'text', value, display } or null if unrecognized.
// ctx: { pa, ma, brave, faith, targetFaith, wp, weaponType, paUnarmed }.
export function legacyAbilityFormula(name, ctx = {}) {
  const { pa = 0, ma = 0, brave = 0, faith = 0, targetFaith = 0,
    wp = 0, weaponType = '', paUnarmed = 0 } = ctx;
  const dmg = (v) => ({ kind: 'damage', value: v, display: String(v) });
  const pct = (v) => ({ kind: 'percent', value: v, display: v + '%' });

  if (/^(Level|Vertical) Jump[1-8]$/.test(name)) {
    if (weaponType === 'Spear') return dmg(Math.floor(pa * wp * 1.5));
    if (wp) return dmg(Math.floor(pa * wp));
    return dmg(Math.floor(paUnarmed * Math.floor((paUnarmed * brave) / 100)));
  }
  switch (name) {
    case 'Elixir': return { kind: 'text', value: 0, display: 'Full Heal' };
    // Blastar Punch / Icewolf Bite / Shellbust Stab / Hellcry Crush now parse from
    // the API ("If successful, Damage (PA * WP)"); only Hellcry Punch is API-absent.
    case 'Hellcry Punch': return dmg(pa * wp);
    case 'Weapon Break': return pct(wp ? pa + wp + 32 : pa + 32);
    case 'Armor Break':  return pct(wp ? pa + wp + 48 : pa + 48);
    case 'Head Break':   return pct(wp ? pa + wp + 40 : pa + 40);
    case 'Shield Break': return pct(wp ? pa + wp + 56 : pa + 56);
    case 'Magic Break':  return pct(pa + 50);
    // Throw (Axe/Wand/Stick/Sword/Staff/Spear/Hammer/Ninja Sword/Knife/Shuriken/
    // Bomb/Dictionary) now parses from the API info (Speed * ThrownWP) — see THROWN_WP.
    case 'Golem':          return pct(Math.floor((faith / 100) * (ma + 225)));
    case 'Toxic Frog':     return pct(Math.floor(((faith * targetFaith) / 10000) * (ma + 110)));
    case 'Bio Sanctus':    return pct(Math.floor(((faith * targetFaith) / 10000) * (ma + 120)));
    case 'Blind Rage':     return pct(Math.floor(((faith * targetFaith) / 10000) * (ma + 135)));
    case 'Confusion Song': return pct(Math.floor(((faith * targetFaith) / 10000) * (ma + 160)));
    case 'Silence Song':   return pct(Math.floor(((faith * targetFaith) / 10000) * (ma + 180)));
    case 'Ice Soul': return dmg(ma * 3);
    default: return null;
  }
}
